import React from "react";
import Select, { components, OptionProps, ControlProps } from "react-select";
import { PacketBadge } from "shared";
import { PACKET_ENUM } from "enums";
import "./InvestmentClientFilter.scss";

export interface InvestmentClientOption {
  label: string;
  value: string;
  id: number;
  uuid: string;
  name: string;
  package: string | null;
}

interface InvestmentClientFilterProps {
  options: InvestmentClientOption[] | undefined;
  value: InvestmentClientOption | null;
  onChange: (opt: InvestmentClientOption | null) => void;
  placeholder?: string;
}

const CustomOption = (props: OptionProps<InvestmentClientOption>) => {
  const { data, innerRef, innerProps, isFocused } = props;
  return (
    <div
      ref={innerRef}
      {...innerProps}
      className={`icf-option${isFocused ? " icf-option--focused" : ""}`}
    >
      <span className="icf-option__name">{data.name}</span>
      {data.package && <PacketBadge packetType={data.package as PACKET_ENUM} />}
      <span className="icf-option__id">ID: {data.id}</span>
    </div>
  );
};

const CustomControl = (props: ControlProps<InvestmentClientOption>) => {
  return (
    <components.Control {...props} className="icf-control">
      {props.children}
    </components.Control>
  );
};

const InvestmentClientFilter: React.FC<InvestmentClientFilterProps> = ({
  options,
  value,
  onChange,
  placeholder = "Filtruj klienta...",
}) => {
  const [inputValue, setInputValue] = React.useState("");

  const filteredCount = React.useMemo(() => {
    if (!inputValue) return (options ?? []).length;
    const lower = inputValue.toLowerCase();
    return (options ?? []).filter(
      (o) =>
        o.name.toLowerCase().includes(lower) || String(o.id).includes(lower),
    ).length;
  }, [options, inputValue]);

  return (
    <div className="icf">
      <Select<InvestmentClientOption>
        options={options}
        value={null}
        onChange={(opt) => onChange(opt as InvestmentClientOption | null)}
        placeholder={placeholder}
        isClearable={false}
        isSearchable
        inputValue={inputValue}
        onInputChange={(val) => setInputValue(val)}
        components={{
          Option: CustomOption,
          Control: CustomControl,
          IndicatorSeparator: () => null,
          NoOptionsMessage: () => (
            <span
              style={{
                padding: "8px 12px",
                display: "block",
                color: "#515151",
                fontFamily: "Source Sans Pro",
                fontSize: "13px",
                fontStyle: "normal",
                fontWeight: 400,
                lineHeight: "19.5px" /* 150% */,
              }}
            >
              Brak klientów spełniających kryteria
            </span>
          ),
        }}
        classNamePrefix="icf-select"
        styles={{
          control: (base) => ({
            ...base,
            cursor: "pointer",
          }),
          input: (base) => ({
            ...base,
            cursor: "pointer",
          }),
          menu: (base) => ({
            ...base,
            width: 280,
            height: 271,
            paddingTop: "3.993px",
            paddingRight: "15.776px",
            overflowY: "auto",
            boxShadow:
              filteredCount > 0 ? "0 4px 16px rgba(0,0,0,0.1)" : "none",
            border: filteredCount > 0 ? "1.052px solid #e0e0e0" : "0px",
            borderRadius: 6,
            zIndex: 9999,
          }),
          menuList: (base) => ({
            ...base,
            padding: 0,
            width: "100%",
            maxHeight: 271,
          }),
        }}
      />
      {value && (
        <div className="icf__selected-badge">
          <div className="icf__selected-badge-info">
            <span className="icf__selected-badge-name">{value.name}</span>
            <span className="icf__selected-badge-id">
              &nbsp;(ID: {value.id})&nbsp;
            </span>
          </div>
          <button
            className="icf__selected-badge-close"
            onClick={() => onChange(null)}
            aria-label="Usuń filtr klienta"
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
};

export default InvestmentClientFilter;
